import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  FormControl
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { PreorderEventItem } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';
import { 
  notifyPriceQuantityUpdateStatus 
} from '../../../actions/itemActions/ItemActions';

interface EditQuantityDialogProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  onUpdate: (updateData: {
    id: number;
    totalQuantity: number;
  }) => void;
  item: PreorderEventItem | null;
  isLoading: boolean;
}

const EditQuantityDialog: React.FC<EditQuantityDialogProps> = ({
  open,
  onClose,
  onUpdate,
  item,
  isLoading
}) => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  
  // Form state - only quantity related
  const [totalQuantity, setTotalQuantity] = useState('');
  const [adjustQuantity, setAdjustQuantity] = useState('0');

  // Loading states
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  // Error states
  const [errors, setErrors] = useState({
    adjustQuantity: false
  });

  // Initialize form when dialog opens
  useEffect(() => {
    if (open && item) {
      const initTotalQuantity = item.stock; // Use stock from preorder item
      setTotalQuantity(initTotalQuantity.toString());
      setAdjustQuantity('0');
    }
  }, [open, item]);

  const handleNumericChange = (field: string, value: string) => {
    // Allow empty string, digits only
    const isValidInput = /^$|^\d*$/.test(value);
    
    if (!isValidInput) return;

    // Update the appropriate state
    switch (field) {
      case 'adjustQuantity':
        setAdjustQuantity(value);
        if (errors.adjustQuantity) {
          setErrors(prev => ({ ...prev, adjustQuantity: false }));
        }
        break;
    }
  };

  // Handle quantity adjustment (add/remove)
  const handleQuantityAdjustment = async (adjustValue: number, type: 'add' | 'remove') => {
    if (!item || adjustValue === 0) return;

    setLoadingUpdate(true);

    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const token = localStorage.token;
      const headers = { Authorization: `Bearer ${token}` };

      // Calculate the new quantity
      let quantity = adjustValue;
      if (type === 'remove') {
        quantity = -adjustValue; // Make it negative for removal
      }

      const payload = {
        id: item.id,
        quantity: quantity
      };

      const response = await axios.put(`${apiURL}/admin/updateIndividualItem`, payload, { headers });

      if (response.status === 200) {
        // Update the UI with the new quantity locally
        const currentQty = Number(totalQuantity);
        const newQty = type === 'add' ? currentQty + adjustValue : currentQty - adjustValue;
        setTotalQuantity(Math.max(0, newQty).toString());
        setAdjustQuantity('0');

        // Close dialog with refresh to get updated data
        handleClose(true);
      }
    } catch (error) {
      dispatch(notifyPriceQuantityUpdateStatus('Error adjusting quantity'));
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleClose = (shouldRefresh: boolean = false) => {
    if (!isLoading && !loadingUpdate) {
      onClose(shouldRefresh);
    }
  };

  const handleDialogClose = () => {
    handleClose(false); // Dialog close (ESC, backdrop click) doesn't refresh
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('preorderEventDetails.edit-quantity')}
      </DialogTitle>
      <DialogContent>
        {item && (
          <>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              <strong>{item.name}</strong> ({item.sku})
            </Typography>
            
            <Grid container spacing={3}>
              {/* Total Quantity Section (Read-only) */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                  {t('items.quantity')}:
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  value={totalQuantity}
                  disabled={true} // Always disabled - quantity updates through adjust controls
                  onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                  style={{ backgroundColor: '#f5f5f5' }} // Gray background to indicate disabled
                />
              </Grid>

              {/* Adjust Quantity Section */}
              <Grid item xs={12}>
                <FormControl fullWidth style={{ marginTop: 16 }}>
                  <Typography variant="caption" color="initial" style={{ marginBottom: 8 }}>
                    {t('items.adjust-qty')}
                  </Typography>
                  <TextField
                    type="text"
                    value={adjustQuantity}
                    onChange={(e) => handleNumericChange('adjustQuantity', e.target.value)}
                    disabled={isLoading || loadingUpdate}
                    onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                    style={{ width: 120 }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RemoveCircleOutlineIcon />}
          disabled={Number(adjustQuantity) === 0 || isLoading || loadingUpdate}
          onClick={() => handleQuantityAdjustment(Number(adjustQuantity), 'remove')}
        >
          {t('items.remove')}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          disabled={Number(adjustQuantity) === 0 || isLoading || loadingUpdate}
          onClick={() => handleQuantityAdjustment(Number(adjustQuantity), 'add')}
        >
          {t('items.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditQuantityDialog;